import React, { useCallback, useEffect, useState } from "react";
import DataSetupMainCard from "./DataSetupMainCard";
import DataSetupDrawer from "./DataSetupDrawer";
import { Label, MuiTable } from "components";
import {
  IconButton,
  Tooltip,
  Box,
  Typography,
  Switch,
  CircularProgress,
  Chip,
  useTheme,
  Grid,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CategoryServices from "services/CategoryService";
import { Dialog, Button, Grow, Divider, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TextValidator from "components/TextValidator";
import FileUpload from "components/FileUpload";
import ImageLoader from "components/ImageLoader";
import StatusConfirmDialog from "components/StatusConfirmDialog";

const CategorySetupCard = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState(null); // Store ID of category being edited
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search: null,
  });

  const [drawer, setDrawer] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    displayOrder: "",
    image: [],
    currentImage: "", // For displaying existing image in edit mode
  });

  // Status update state
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    loading: false,
    categoryId: null,
    categoryName: "",
    newStatus: null,
  });

  // View modal state
  const [viewModal, setViewModal] = useState({
    open: false,
    item: null,
  });

  const setPage = (page) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: page,
    }));
  };

  const setPageSize = (pageSize) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: 1,
      limit: pageSize,
    }));
  };

  const getData = useCallback(async () => {
    setLoading(true);
    const response = await CategoryServices.getAll({
      ...filters,
      page: filters.page - 1,
    });
    console.log("response", response);

    if (response.success) {
      setData(response?.data?.view?.data || []);
      setTotalItems(response?.data?.view?.totalItems || 0);
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (files) => {
    setFormData((prev) => ({
      ...prev,
      image: files,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const formPayload = new FormData();
      formPayload.append("name", formData.name);
      formPayload.append("description", formData.description);
      formPayload.append("displayOrder", formData.displayOrder);
      formPayload.append("companyId", filters.companyId);

      if (formData.image.length > 0) {
        formPayload.append("image", formData.image[0]);
      }

      if (isEdit) {
        await CategoryServices.update(editingId, formPayload);
      } else {
        await CategoryServices.create(formPayload);
      }

      setFormData({
        name: "",
        description: "",
        displayOrder: "",
        image: [],
        currentImage: "",
      });
      setEditingId(null); // Clear editing ID after submission
      setDrawer(false);
      getData();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleEditClick = (item) => {
    setEditingId(item.id); // Set the ID of item being edited
    setFormData({
      name: item.name || "",
      description: item.description || "",
      displayOrder: item.displayOrder || "",
      image: [],
      currentImage: item.image || "", // Store current image path
    });
    setIsEdit(true);
    setDrawer(true);
  };

  const handleAddClick = () => {
    setEditingId(null); // Clear editing ID for add mode
    setFormData({
      name: "",
      description: "",
      displayOrder: "",
      image: [],
      currentImage: "", // Clear current image for add mode
    });
    setIsEdit(false);
    setDrawer(true);
  };

  /**
   * Handle view button click
   * Opens modal to display category details
   */
  const handleViewClick = (item) => {
    setViewModal({
      open: true,
      item: item,
    });
  };

  /**
   * Handle status toggle button click
   * Opens confirmation dialog before updating status
   */
  const handleStatusToggle = (item) => {
    const newStatus = !item.isActive;
    setStatusDialog({
      open: true,
      loading: false,
      categoryId: item.id,
      categoryName: item.name,
      newStatus: newStatus,
    });
  };

  /**
   * Confirm status change and call API
   */
  const handleStatusConfirm = async () => {
    const { categoryId, newStatus } = statusDialog;

    try {
      setStatusDialog((prev) => ({ ...prev, loading: true }));

      const payload = {
        isActive: newStatus,
        companyId: filters.companyId,
      };

      const response = await CategoryServices.updateStatus(categoryId, payload);

      if (response.success) {
        // Refresh data to get updated status
        await getData();
        console.log("✅ Category status updated successfully");
      } else {
        console.error("❌ Failed to update category status:", response.error);
      }

      setStatusDialog((prev) => ({ ...prev, open: false, loading: false }));
    } catch (error) {
      console.error("Error updating status:", error);
      setStatusDialog((prev) => ({ ...prev, open: false, loading: false }));
    }
  };

  return (
    <DataSetupMainCard
      iconSrc="/assets/images/icons/category.png"
      title="Category"
      subtitle="Manage your categories"
      actionIconSrc="/assets/images/icons/plus.png"
      actionText="Add New Category"
      onAddClick={handleAddClick}
    >
      <MuiTable
        columns={[
          {
            accessorKey: "actions",
            header: "Actions",
            Cell: ({ cell }) => {
              const item = cell.row.original;
              const isUpdating =
                statusDialog.categoryId === item.id && statusDialog.loading;

              return (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Tooltip title="View">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={() => handleViewClick(item)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditClick(item)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={item.isActive ? "Deactivate" : "Activate"}>
                    <Box sx={{ position: "relative" }}>
                      <Switch
                        checked={item.isActive}
                        size="small"
                        disabled={isUpdating}
                        onChange={() => handleStatusToggle(item)}
                      />
                      {isUpdating && (
                        <CircularProgress
                          size={20}
                          sx={{
                            position: "absolute",
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            opacity: 0.7,
                          }}
                        />
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              );
            },
          },
          {
            accessorKey: "name",
            header: "Name",
          },
          {
            accessorKey: "description",
            header: "Description",
          },
          {
            accessorKey: "displayOrder",
            header: "Display Order",
          },
          {
            accessorKey: "image",
            header: "Image",
            Cell: ({ cell }) => (
              <ImageLoader
                imagePath={cell.getValue()}
                width={80}
                height={80}
                alt={cell.row.original.name}
                rounded={true}
                borderRadius={1}
              />
            ),
          },
          {
            accessorKey: "isActive",
            header: "Status",
            Cell: ({ cell }) => {
              const isActive = cell.getValue();

              return (
                <Chip
                  label={isActive ? "Active" : "Inactive"}
                  variant="filled"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: 0.3,
                    background: isActive
                      ? `linear-gradient(135deg, ${theme.palette.success.main}15 0%, ${theme.palette.success.light}15 100%)`
                      : `linear-gradient(135deg, ${theme.palette.warning.main}15 0%, ${theme.palette.warning.light}15 100%)`,
                    color: isActive
                      ? theme.palette.success.dark
                      : theme.palette.warning.dark,
                    border: `2px solid ${
                      isActive
                        ? theme.palette.success.main
                        : theme.palette.warning.main
                    }`,
                    borderRadius: "20px",
                    padding: "4px 8px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: isActive
                        ? `0 4px 12px ${theme.palette.success.main}40`
                        : `0 4px 12px ${theme.palette.warning.main}40`,
                    },
                  }}
                />
              );
            },
          },
        ]}
        data={data}
        title=""
        manualPagination={true}
        rowCount={totalItems}
        currentPage={filters.page}
        pageSize={filters.limit}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        loading={loading}
      />

      <DataSetupDrawer
        maxWidth="md"
        open={drawer}
        onClose={() => setDrawer(false)}
        iconSrc="/assets/images/icons/category.png"
        title={isEdit ? "Edit Category" : "Add Category"}
        subtitle="Create and manage category details"
        submitLabel={isEdit ? "Update" : "Create"}
        onSubmit={handleFormSubmit}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box gap={1} display="flex" flexDirection="column">
              {/* Category Name */}
              <TextValidator
                label="Category Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                validators={["required", "minStringLength:2"]}
                errorMessages={[
                  "Category name is required",
                  "Category name must be at least 2 characters",
                ]}
                fullWidth
                size="small"
                placeholder="Enter category name"
              />

              {/* Description */}
              <TextValidator
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                validators={["required"]}
                errorMessages={["Description is required"]}
                fullWidth
                size="small"
                multiline
                rows={3}
                placeholder="Describe the category"
              />

              {/* Display Order */}
              <TextValidator
                label="Display Order"
                name="displayOrder"
                value={formData.displayOrder}
                onChange={handleInputChange}
                validators={["required", "isNumber"]}
                errorMessages={[
                  "Display order is required",
                  "Display order must be a number",
                ]}
                fullWidth
                size="small"
                type="number"
                placeholder="Enter display order"
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {/* Image Upload */}
            <Box>
              <FileUpload
                value={formData.image}
                onChange={handleImageChange}
                multiple={false}
                required={false}
                acceptedFileTypes={["image/jpeg", "image/png", "image/webp"]}
                maxSingleFileSize={5 * 1024 * 1024}
                label={
                  formData.image.length > 0
                    ? "Change Image"
                    : "Upload Category Image"
                }
                helperText="Accepted formats: JPG, PNG, WEBP (Max 5MB)"
              />
            </Box>
          </Grid>
          <Grid size={12}>
            {isEdit && formData.currentImage && (
              <Box
                sx={{
                  mb: 2,
                  // p: 2,
                  // backgroundColor: theme.palette.action.hover,
                  borderRadius: 2,
                  // border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Label>Current Image Preview</Label>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "start",
                    borderRadius: 1,
                    overflow: "hidden",
                    mt: 1,
                  }}
                >
                  <ImageLoader
                    imagePath={formData.currentImage}
                    width={80}
                    height={80}
                    alt="Current category image"
                    rounded={true}
                    borderRadius={1}
                  />
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
        <></>
      </DataSetupDrawer>

      {/* Status Confirmation Dialog */}
      <StatusConfirmDialog
        open={statusDialog.open}
        onClose={() => setStatusDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={handleStatusConfirm}
        title={
          statusDialog.newStatus ? "Activate Category" : "Deactivate Category"
        }
        message={
          statusDialog.newStatus
            ? "Are you sure you want to activate this category? It will be visible to customers."
            : "Are you sure you want to deactivate this category? It will no longer be visible to customers."
        }
        itemName={statusDialog.categoryName}
        newStatus={statusDialog.newStatus}
        loading={statusDialog.loading}
      />

      {/* View Category Modal */}
      <Dialog
        open={viewModal.open}
        onClose={() => setViewModal({ open: false, item: null })}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Grow}
        transitionDuration={{
          enter: 400,
          exit: 300,
        }}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            maxHeight: "90vh",
            boxShadow: `0 20px 60px rgba(0, 0, 0, 0.15)`,
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            transition: "all 0.4s ease-in-out",
          },
        }}
      >
        {/* ===== Header with Icon ===== */}
        <Box
          sx={{
            p: 3,
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Icon Container */}
          <Box
            component="img"
            src="/assets/images/icons/category.png"
            alt="Category Icon"
            sx={{
              width: 48,
              height: 48,
              minWidth: 48,
              objectFit: "contain",
              filter:
                "brightness(0) saturate(100%) invert(43%) sepia(99%) saturate(1235%) hue-rotate(244deg)",
              opacity: 0.8,
            }}
          />

          {/* Title and Subtitle */}
          <Box sx={{ flexGrow: 1, pr: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 0.5,
                letterSpacing: 0.2,
              }}
            >
              Category Details
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.875rem",
              }}
            >
              View category information
            </Typography>
          </Box>

          {/* Close Button */}
          <IconButton
            onClick={() => setViewModal({ open: false, item: null })}
            sx={{
              mt: -0.5,
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: `${theme.palette.primary.main}20`,
                color: theme.palette.primary.main,
              },
              transition: "all 0.2s ease",
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ===== Content ===== */}
        <Box
          sx={{
            p: 3,
            flexGrow: 1,
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.background.default,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.divider,
              borderRadius: 3,
              "&:hover": {
                backgroundColor: theme.palette.text.disabled,
              },
            },
          }}
        >
          {viewModal.item && (
            <Stack spacing={3}>
              {/* Category Image */}
              {viewModal.item.image && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: 1,
                  }}
                >
                  <ImageLoader
                    imagePath={viewModal.item.image}
                    width={150}
                    height={150}
                    alt={viewModal.item.name}
                    rounded={true}
                    borderRadius={2}
                  />
                </Box>
              )}

              {/* Category Name */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.name}
                </Typography>
              </Box>

              {/* Description */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Description
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.6,
                  }}
                >
                  {viewModal.item.description}
                </Typography>
              </Box>

              {/* Display Order */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Display Order
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.displayOrder}
                </Typography>
              </Box>

              {/* Status */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Status
                </Typography>
                <Chip
                  label={viewModal.item.isActive ? "Active" : "Inactive"}
                  variant="filled"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: 0.3,
                    background: viewModal.item.isActive
                      ? `linear-gradient(135deg, ${theme.palette.success.main}15 0%, ${theme.palette.success.light}15 100%)`
                      : `linear-gradient(135deg, ${theme.palette.warning.main}15 0%, ${theme.palette.warning.light}15 100%)`,
                    color: viewModal.item.isActive
                      ? theme.palette.success.dark
                      : theme.palette.warning.dark,
                    border: `2px solid ${
                      viewModal.item.isActive
                        ? theme.palette.success.main
                        : theme.palette.warning.main
                    }`,
                    borderRadius: "20px",
                    padding: "4px 8px",
                  }}
                />
              </Box>
            </Stack>
          )}
        </Box>

        <Divider />

        {/* ===== Footer ===== */}
        <Box
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Button
            variant="contained"
            onClick={() => setViewModal({ open: false, item: null })}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              py: 1.2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              color: "#ffffff",
              fontSize: "0.95rem",
              letterSpacing: 0.5,
              boxShadow: `0 4px 15px rgba(102, 126, 234, 0.3)`,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 8px 25px rgba(102, 126, 234, 0.4)`,
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
              },
              "&:active": {
                transform: "translateY(0px)",
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Dialog>
    </DataSetupMainCard>
  );
};

export default CategorySetupCard;
